<script setup lang="ts">
import { CodeExample } from '@scalar/blocks/code-example'
import { ScalarErrorBoundary } from '@scalar/components/error-boundary'
import { ScalarIconButton } from '@scalar/components/icon-button'
import { ScalarMarkdown } from '@scalar/components/markdown'
import {
  ScalarIconCopy,
  ScalarIconPlay,
  ScalarIconWebhooksLogo,
} from '@scalar/icons'
import { useClipboard } from '@scalar/use-hooks/useClipboard'
import { getResolvedRef } from '@scalar/workspace-store/helpers/get-resolved-ref'
import type { SecuritySchemeObjectSecret } from '@scalar/workspace-store/request-example'
import type {
  OpenApiDocument,
  OperationObject,
  ServerObject,
} from '@scalar/workspace-store/schemas/v3.2/strict/openapi-document'
import { computed, provide, ref } from 'vue'

import { Anchor } from '@/components/Anchor'
import { Badge } from '@/components/Badge'
import { HttpMethod } from '@/components/HttpMethod'
import { LinkList } from '@/components/LinkList'
import OperationPath from '@/components/OperationPath.vue'
import { SectionAccordion } from '@/components/Section'
import { useDocumentOutline } from '@/features/document-outline'
import { ExampleResponses } from '@/features/example-responses'
import { ExternalDocs } from '@/features/external-docs'
import { useLocalization } from '@/features/localization'
import Callbacks from '@/features/Operation/components/callbacks/Callbacks.vue'
import CopyMarkdownButton from '@/features/Operation/components/CopyMarkdownButton.vue'
import OperationParameters from '@/features/Operation/components/OperationParameters.vue'
import OperationResponses from '@/features/Operation/components/OperationResponses.vue'
import OperationScopes from '@/features/Operation/components/OperationScopes.vue'
import SecurityRequirementBadge from '@/features/Operation/components/SecurityRequirementBadge.vue'
import {
  getRequiredScopeGroups,
  type RequiredSecurity,
} from '@/features/Operation/helpers/get-required-security'
import {
  getOperationStability,
  getOperationStabilityColor,
  isOperationDeprecated,
} from '@/features/Operation/helpers/operation-stability'
import type { OperationProps } from '@/features/Operation/Operation.vue'
import {
  REQUEST_BODY_COMPOSITION_INDEX_SYMBOL,
  type RequestBodyCompositionSelection,
} from '@/features/Operation/request-body-composition-index'
import { getXKeysFromObject } from '@/features/specification-extension'
import SpecificationExtension from '@/features/specification-extension/SpecificationExtension.vue'
import { TestRequestButton } from '@/features/test-request-button'
import { XBadges } from '@/features/x-badges'

const {
  clientOptions,
  document,
  eventBus,
  isWebhook,
  method,
  operation,
  options,
  path,
  requiredSecurity,
  selectedServer,
  selectedSecuritySchemes,
  selectedClient,
  selectedExample,
} = defineProps<
  Omit<
    OperationProps,
    'document' | 'pathValue' | 'server' | 'securitySchemes' | 'authStore'
  > & {
    /** Operation object with path params */
    operation: OperationObject
    /** The selected server for the operation */
    selectedServer: ServerObject | null
    /** The selected security schemes for the operation */
    selectedSecuritySchemes: SecuritySchemeObjectSecret[]
    /** Required/optional security state for the badge next to the path */
    requiredSecurity: RequiredSecurity
    /** The document the operation belongs to, used to resolve schema references for display */
    document?: OpenApiDocument
  }
>()
const { translate } = useLocalization()

const operationTitle = computed(() => operation.summary || path || '')
const operationExtensions = computed(() => getXKeysFromObject(operation))

/** Whether the operation requires any OAuth scopes, used to skip the empty card item. */
const hasRequiredScopes = computed(
  () => getRequiredScopeGroups(requiredSecurity).length > 0,
)

/** Track the selected request body content type so the code sample stays in sync */
const selectedRequestBodyContentType = ref<string | undefined>()

/**
 * The example key actually shown in the request snippet for this operation.
 *
 * The test-request button lives in the accordion header, outside `OperationCodeSample`, so it
 * cannot read the resolved key from the footer slot like the modern layout does. We mirror it here
 * so the button opens the client with the same example the snippet displays, even when this
 * operation does not share the document-wide selection.
 */
const resolvedExampleKey = ref<string>('')

/** Selected request body oneOf/anyOf variants; synced with schema dropdowns and code sample */
const requestBodyCompositionSelection = ref<RequestBodyCompositionSelection>({})

const requestBodyCompositionSelectionForCodeSample = computed(
  (): RequestBodyCompositionSelection => ({
    ...requestBodyCompositionSelection.value,
  }),
)

const requestBodyCompositionSelectionKey = computed(() =>
  JSON.stringify(requestBodyCompositionSelectionForCodeSample.value),
)

provide(REQUEST_BODY_COMPOSITION_INDEX_SYMBOL, requestBodyCompositionSelection)

/**
 * Selected response content type per status code. Shared between the response list (which writes
 * the selection) and the example response panel (which reads it) so the two stay in sync.
 */
const selectedResponseContentTypes = ref<Record<string, string>>({})

const { copyToClipboard } = useClipboard()

const { level: headingLevel } = useDocumentOutline('operation')
</script>
<template>
  <SectionAccordion
    :id="id"
    :aria-label="operationTitle"
    class="reference-endpoint"
    :modelValue="!isCollapsed"
    transparent
    @update:modelValue="
      (value) => eventBus?.emit('toggle:nav-item', { id, open: value })
    ">
    <template #title>
      <div class="operation-title">
        <div class="operation-details">
          <HttpMethod
            class="endpoint-type"
            :method="method"
            short />
          <Anchor
            class="endpoint-anchor"
            @copyAnchorUrl="() => eventBus?.emit('copy-url:nav-item', { id })">
            <component
              :is="`h${headingLevel}`"
              class="endpoint-label">
              <div class="endpoint-label-path">
                <OperationPath
                  :deprecated="isOperationDeprecated(operation)"
                  :path="path" />
              </div>
              <div class="endpoint-label-name">
                {{ operationTitle }}
              </div>
              <!-- Stability badge -->
              <Badge
                v-if="getOperationStability(operation)"
                class="capitalize"
                :class="getOperationStabilityColor(operation)">
                {{ getOperationStability(operation) }}
              </Badge>

              <!-- Webhook badge -->
              <Badge
                v-if="isWebhook"
                class="font-code text-green flex w-fit items-center justify-center gap-1">
                <ScalarIconWebhooksLogo weight="bold" />
                {{ translate('operation.webhook') }}
              </Badge>

              <!-- x-badges before -->
              <XBadges
                :badges="operation['x-badges']"
                position="before" />
            </component>
          </Anchor>
        </div>
      </div>
    </template>
    <template #actions="{ active }">
      <!-- Required auth indicator -->
      <SecurityRequirementBadge
        hideLabel
        :requiredSecurity />
      <!-- x-badges after -->
      <XBadges
        :badges="operation['x-badges']"
        position="after" />
      <template v-if="!options.hideTestRequestButton">
        <TestRequestButton
          v-if="active"
          :id
          :eventBus
          :exampleName="resolvedExampleKey"
          :method
          :path
          :requestBodyCompositionSelection="
            requestBodyCompositionSelectionForCodeSample
          " />
        <ScalarIconPlay
          v-else
          class="endpoint-try-hint size-4.5" />
      </template>
      <span
        v-if="options.showOperationId && operation.operationId"
        class="font-code text-sm">
        {{ operation.operationId }}
      </span>
      <ScalarIconButton
        class="endpoint-copy p-0.5"
        :icon="ScalarIconCopy"
        :label="translate('actions.copyEndpointUrl')"
        size="xs"
        variant="ghost"
        @click.stop="copyToClipboard(path)" />
    </template>
    <template #description>
      <div
        v-if="document"
        class="mb-3 flex justify-end">
        <CopyMarkdownButton
          :document
          :isWebhook
          :method
          :path />
      </div>
      <ScalarMarkdown
        v-if="operation.description"
        :anchorPrefix="id"
        :aria-label="translate('common.description')"
        role="group"
        transformType="heading"
        :value="operation.description"
        withAnchors
        withImages />
    </template>
    <div class="endpoint-content">
      <div class="operation-details-card">
        <div
          v-if="Object.keys(operationExtensions).length > 0"
          class="operation-details-card-item">
          <SpecificationExtension :value="operationExtensions" />
        </div>
        <div
          v-if="hasRequiredScopes"
          class="operation-details-card-item">
          <OperationScopes :requiredSecurity />
        </div>
        <div class="operation-details-card-item">
          <OperationParameters
            v-model:selectedContentType="selectedRequestBodyContentType"
            :breadcrumb="[id]"
            :document
            :eventBus
            :options
            :parameters="operation.parameters"
            :requestBody="getResolvedRef(operation.requestBody)" />
        </div>
        <div class="operation-details-card-item">
          <!-- Responses are disclosures unless the configuration expands every
               response, in which case they render as static panels -->
          <OperationResponses
            v-model:selectedContentTypes="selectedResponseContentTypes"
            :breadcrumb="[id]"
            :collapsableItems="!options.expandAllResponses"
            :document
            :eventBus
            :options
            :responses="operation.responses" />
        </div>

        <!-- Callbacks -->
        <div
          v-if="operation?.callbacks"
          class="operation-details-card-item">
          <Callbacks
            :breadcrumb="[id]"
            :callbacks="operation.callbacks"
            :document
            :eventBus
            :options
            :path />
        </div>
      </div>

      <ExampleResponses
        v-if="operation.responses"
        class="operation-example-card"
        :eventBus
        :responses="operation.responses"
        :selectedContentTypes="selectedResponseContentTypes"
        :selectedExample />

      <!-- New Example Request -->
      <div>
        <!-- External Docs -->
        <LinkList v-if="operation.externalDocs">
          <ExternalDocs :value="operation.externalDocs" />
        </LinkList>
        <!-- Request Example -->
        <ScalarErrorBoundary>
          <CodeExample
            :key="requestBodyCompositionSelectionKey"
            class="operation-example-card"
            :clientOptions
            :codeSampleUnavailable="
              translate('operation.codeSampleUnavailable')
            "
            :eventBus
            fallback
            :isWebhook
            :method
            :operation
            :path
            :requestBodyCompositionSelection="
              requestBodyCompositionSelectionForCodeSample
            "
            :securitySchemes="selectedSecuritySchemes"
            :selectedClient
            :selectedContentType="selectedRequestBodyContentType"
            :selectedExample
            :selectedServer
            @update:exampleKey="resolvedExampleKey = $event" />
        </ScalarErrorBoundary>
      </div>
    </div>
  </SectionAccordion>
</template>

<style scoped>
@reference "@/style.css";

.operation-title {
  display: flex;
  justify-content: space-between;
}
.operation-details {
  display: flex;
  align-items: center;
  margin-top: 0;
  gap: 9px;

  min-width: 0;
  flex-shrink: 1;
}
.operation-details :deep(.endpoint-anchor .scalar-button svg) {
  width: 16px;
  height: 16px;
}
.endpoint-type {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  position: relative;
  z-index: 0;

  width: 60px;
  padding: 6px;
  flex-shrink: 0;

  font-size: var(--scalar-small);

  text-transform: uppercase;
  font-weight: var(--scalar-bold);
  font-family: var(--scalar-font);
}
.endpoint-type::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;

  background: currentColor;
  opacity: 0.15;

  border-radius: var(--scalar-radius);
}

.endpoint-anchor {
  display: flex;
  align-items: center;
  min-width: 0;
  flex-shrink: 1;
}
.endpoint-anchor.label {
  display: flex;
}
.endpoint-label {
  display: flex;
  align-items: baseline;
  gap: 9px;

  min-width: 0;
  flex-shrink: 1;

  color: var(--scalar-color-1);
}

.endpoint-label-path {
  font-family: var(--scalar-font-code);
  font-size: var(--scalar-mini);

  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
.endpoint-label-path :deep(em) {
  color: var(--scalar-color-2);
}
.endpoint-label-name {
  color: var(--scalar-color-2);
  font-size: var(--scalar-small);

  /* Concatenate the name before we shrink the path */
  flex-shrink: 1000000000;

  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.endpoint-try-hint {
  padding: 2px;
  flex-shrink: 0;
}
.endpoint-copy {
  color: currentColor;
}
.endpoint-copy :deep(svg) {
  stroke-width: 2px;
}

.endpoint-content {
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: row;
  gap: 9px;
  padding: 9px;
}

@variant lg {
  .endpoint-content {
    grid-auto-flow: column;
  }
}

@container (max-width: 900px) {
  .endpoint-content {
    grid-template-columns: 1fr;
  }
}

.endpoint-content > * {
  min-width: 0;
}

/*
 * The schema tree inside a classic card.
 *
 * A tree row hangs its +/- control one gutter to the LEFT of its own text. The
 * modern layout absorbs that in the page margin, but a classic card draws its
 * border exactly there, so the controls sat on top of the border and outside
 * the box that is supposed to contain them. Every card that holds tree rows
 * reserves `gutter + half` on the inline start — plus the same 6px of air the
 * modern layout leaves at the page edge, so a control never kisses the border —
 * and matches the card's own 9px on the end so a long signature never touches
 * the border either.
 *
 * The reserve is spelled out at each use site rather than kept as a token: a
 * custom property substitutes where it is DECLARED, so a token defined at app
 * scope would freeze at the wide values and keep reserving 25px in a narrow
 * container that only needs 18 (same reason `schema-rail`'s fade is inline).
 *
 * The `:has()` guard reserves the space only where a tree row can draw a
 * control: a card holding nothing but a description and an example keeps the
 * plain 9px.
 *
 * `.parameter-item--tree` has to be in the guard even though a row of its own
 * hangs nothing: the classic card renders responses as disclosures, and a
 * CLOSED one unmounts its panel, taking every `.property--tree` with it. An
 * operation with only responses would then reserve nothing while its status
 * rows still drew their pucks 25px to the left — over the card's border — and
 * the reserve would appear and disappear as rows were opened, shifting the
 * whole card sideways on a click. The row marker is there whatever the
 * disclosure is doing, so the reserve holds still.
 */
.endpoint-content:has(
  .property--tree,
  .parameter-item--tree,
  .callback-list-item--tree
) {
  padding-inline-start: calc(
    var(--schema-gutter, 16px) + var(--schema-glyph-half, 9px) + 6px
  );
}

.operation-details-card-item :deep(.request-body-schema:has(.property--tree)),
.operation-details-card-item
  :deep(.callbacks-list:has(.callback-list-item--tree)) {
  padding-inline-start: calc(
    var(--schema-gutter, 16px) + var(--schema-glyph-half, 9px) + 6px
  );
  padding-inline-end: 9px;
}

/*
 * The trailing copy-link is a hover-only affordance, and it is a flex item of
 * the heading, so it competes for the line with the property name and its type.
 * The classic card is roughly 90px narrower than the modern column, and there
 * that 22px is enough to push a signature onto a second line — on a control
 * that is transparent until hovered, so the row just reads as a blank line
 * under the name. Rows stay deep-linkable: the anchor id is on the row itself,
 * and only the click-to-copy button goes, exactly as it does for a coarse
 * pointer (see CopyLinkButton.vue).
 */
.endpoint-content :deep(.copy-link-trailing) {
  display: none;
}

.operation-details-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}
.operation-details-card-item :deep(.parameter-list),
.operation-details-card-item :deep(.callbacks-list) {
  border: var(--scalar-border-width) solid var(--scalar-border-color);
  border-radius: var(--scalar-radius-lg);
  margin-top: 0;
}

.operation-details-card-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.operation-details-card-item :deep(.parameter-list-items) {
  margin-bottom: 0;
}
.operation-details-card :deep(.parameter-item:last-of-type .parameter-schema) {
  padding-bottom: 12px;
}
.operation-details-card :deep(.parameter-list .parameter-list) {
  margin-bottom: 12px;
}
.operation-details-card :deep(.parameter-item) {
  margin: 0;
  padding: 0;
}
.operation-details-card :deep(.property) {
  padding: 9px;
  margin: 0;
}
.operation-details-card :deep(.parameter-list-title),
.operation-details-card :deep(.request-body-title),
.operation-details-card :deep(.callbacks-title) {
  text-transform: uppercase;
  font-weight: var(--scalar-bold);
  font-size: var(--scalar-mini);
  color: var(--scalar-color-2);
  line-height: 1.33;
  padding: 9px;
  margin: 0;
}

.operation-details-card :deep(.request-body-description) {
  margin-top: 0;
  padding: 9px 9px 0 9px;
  border-top: var(--scalar-border-width) solid var(--scalar-border-color);
}

.operation-details-card :deep(.request-body) {
  margin-top: 0;
  border-radius: var(--scalar-radius-lg);
  border: var(--scalar-border-width) solid var(--scalar-border-color);
}
.operation-details-card
  :deep(.request-body .schema-card--level-0 > .schema-card-description) {
  padding-inline: 9px;
}

.operation-details-card :deep(.request-body-header) {
  padding-bottom: 0;
  border-bottom: 0;
}

.operation-details-card :deep(.contents button) {
  margin-right: 9px;
}

.operation-details-card
  :deep(.schema-card--open + .schema-card:not(.schema-card--open)) {
  margin-inline: 9px;
  margin-bottom: 9px;
}
.operation-details-card :deep(.request-body-schema .property--level-0) {
  padding: 0;
}
.operation-details-card :deep(.request-body-schema .property--level-0) {
  padding: 0;
}

.operation-details-card :deep(.selected-content-type) {
  margin-right: 9px;
}

.operation-example-card {
  position: sticky;
  top: calc(var(--refs-viewport-offset) + 24px);
  max-height: calc(var(--refs-viewport-height) - 48px);
}

@media (max-width: 600px) {
  .operation-example-card {
    max-height: unset;
    position: static;
  }
}
</style>
